import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { Issue, IssueDetail, Template } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { AssigneePicker, type Assignment } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Modal, Notice, Spinner, StatusBadge } from '../components/ui.js'
import { formatDate, formatDateTime, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { TaskModal } from './TaskDialogs.js'

const FILTERS = [
  { value: 'active', label: 'Aktiv' },
  { value: 'open', label: 'Offen' },
  { value: 'in_progress', label: 'In Arbeit' },
  { value: 'waiting', label: 'Rückfrage' },
  { value: 'done', label: 'Erledigt' },
  { value: 'all', label: 'Alle' },
]

const CATEGORY_LABEL: Record<string, string> = {
  besichtigung: 'Wohnungsbesichtigung',
  haushaltshilfe: 'Unterstützung im Haushalt',
  reparatur: 'Reparatur oder Mangel',
  sonstiges: 'Sonstiges',
}

type Kind = 'mangel' | 'ticket'

/**
 * Mängel from the app and the notice board, and Anliegen from the customer
 * portal. Both land here; an Anliegen has a conversation with the customer
 * and usually turns into a task.
 */
export function IssuesPage() {
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const [kind, setKind] = useState<Kind>(params.get('art') === 'anliegen' ? 'ticket' : 'mangel')
  const [filter, setFilter] = useState('active')
  const [selected, setSelected] = useState<string | null>(null)
  const [openTask, setOpenTask] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['issues', kind, filter],
    queryFn: () => api<Issue[]>(`/api/issues?kind=${kind}&status=${filter}`),
  })
  const counts = useQuery({
    queryKey: ['issues', 'counts'],
    queryFn: async () => {
      const [mangel, ticket] = await Promise.all([
        api<Issue[]>('/api/issues?kind=mangel&status=active'),
        api<Issue[]>('/api/issues?kind=ticket&status=active'),
      ])
      return { mangel: mangel.length, ticket: ticket.length, answers: ticket.filter((t) => t.lastFromCustomer || t.status === 'open').length }
    },
  })

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['issues'] })
    await queryClient.invalidateQueries({ queryKey: ['issue'] })
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  return (
    <>
      <PageHead title="Mängel und Anliegen" subtitle="Mängel aus der App und vom Aushang, Anliegen der Kunden aus dem Portal." />

      <div className="toolbar">
        <div className="toolbar-row">
          <div className="segmented" role="group" aria-label="Art">
            <button type="button" aria-pressed={kind === 'mangel'} onClick={() => setKind('mangel')}>
              Mängel <span className="muted">{counts.data?.mangel ?? ''}</span>
            </button>
            <button type="button" aria-pressed={kind === 'ticket'} onClick={() => setKind('ticket')}>
              Anliegen von Kunden <span className="muted">{counts.data?.ticket ?? ''}</span>
              {Boolean(counts.data?.answers) && <span className="dot-alert" title="Neue Anliegen oder Antworten" />}
            </button>
          </div>
          <div className="grow" />
          <div className="quick-range">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`btn ${filter === option.value ? '' : 'secondary'} small`}
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="card">
        {isLoading ? (
          <Spinner />
        ) : !data || data.length === 0 ? (
          <EmptyState
            title={kind === 'ticket' ? 'Keine Anliegen in dieser Ansicht' : 'Keine Mängel in dieser Ansicht'}
            hint={
              kind === 'ticket'
                ? 'Kunden melden Anliegen im Kundenportal. Zugänge legen Sie beim Kunden unter Objekte an.'
                : 'Mitarbeiter melden Mängel in der App, Bewohner über den Aushang.'
            }
          />
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Eingang</th>
                  <th>{kind === 'ticket' ? 'Anliegen' : 'Problem'}</th>
                  <th>Objekt</th>
                  <th>Von</th>
                  {kind === 'ticket' && <th className="nowrap">Wunschtermin</th>}
                  <th className="nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((issue) => (
                  <tr key={issue.id} className="clickable" onClick={() => setSelected(issue.id)}>
                    <td className="nowrap small">{formatDateTime(issue.createdAt)}</td>
                    <td>
                      <span className="strong">{issue.title}</span>
                      {issue.kind === 'ticket' && (
                        <div className="muted small">
                          {CATEGORY_LABEL[issue.category ?? ''] ?? 'Anliegen'}
                          {issue.messageCount > 0 ? `, ${issue.messageCount} Nachrichten` : ''}
                        </div>
                      )}
                      {issue.photoCount > 0 && <span className="muted small"> {issue.photoCount} Foto(s)</span>}
                      {issue.kind === 'ticket' && issue.lastFromCustomer && issue.status !== 'done' && (
                        <span className="badge warn" style={{ marginLeft: 6 }}>
                          Antwort des Kunden
                        </span>
                      )}
                    </td>
                    <td>
                      {issue.siteName}
                      <div className="muted small">{issue.path.length > 0 ? issue.path.join(' › ') : 'Liegenschaft'}</div>
                    </td>
                    <td className="small">{issue.reportedByName ?? '–'}</td>
                    {kind === 'ticket' && (
                      <td className="nowrap">{issue.requestedDate ? formatDate(issue.requestedDate) : <span className="muted">–</span>}</td>
                    )}
                    <td className="nowrap">
                      <StatusBadge status={issue.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <IssueModal
        issueId={selected}
        onClose={() => setSelected(null)}
        onChanged={refresh}
        onOpenTask={(id) => {
          setSelected(null)
          setOpenTask(id)
        }}
      />
      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={async () => {
          setOpenTask(null)
          await refresh()
        }}
      />
    </>
  )
}

export function IssueModal({
  issueId,
  onClose,
  onChanged,
  onOpenTask,
}: {
  issueId: string | null
  onClose: () => void
  onChanged: () => Promise<void> | void
  onOpenTask: (id: string) => void
}) {
  const { data: issue } = useQuery({
    queryKey: ['issue', issueId],
    queryFn: () => api<IssueDetail>(`/api/issues/${issueId}`),
    enabled: Boolean(issueId),
  })
  return (
    <Modal open={issueId !== null} title={issue?.title ?? 'Mangel'} onClose={onClose}>
      {!issue ? <Spinner /> : <IssueBody issue={issue} onChanged={onChanged} onOpenTask={onOpenTask} onClose={onClose} />}
    </Modal>
  )
}

function IssueBody({
  issue,
  onChanged,
  onOpenTask,
  onClose,
}: {
  issue: IssueDetail
  onChanged: () => Promise<void> | void
  onOpenTask: (id: string) => void
  onClose: () => void
}) {
  const toast = useToast()
  const [text, setText] = useState('')
  const [planning, setPlanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const closed = issue.status === 'done' || issue.status === 'rejected'
  const isTicket = issue.kind === 'ticket'

  const send = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/issues/${issue.id}/messages`, { method: 'POST', body }),
    onSuccess: async (_r, body) => {
      setText('')
      setError(null)
      toast(body.internal ? 'Notiz gespeichert.' : body.status === 'waiting' ? 'Rückfrage gesendet.' : body.status ? 'Status geändert, Kunde informiert.' : 'Antwort gesendet.')
      await onChanged()
      if (body.status === 'done' || body.status === 'rejected') onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Senden fehlgeschlagen.'),
  })

  // Mängel keep the simple status change; a note is optional there.
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/issues/${issue.id}`, { method: 'PATCH', body }),
    onSuccess: async () => {
      await onChanged()
      onClose()
    },
  })

  return (
    <>
      <div className="spread" style={{ marginBottom: 12 }}>
        <div>
          <div className="strong">
            <Link to={`/objekte/${issue.siteId ?? issue.objectId}`}>{issue.siteName}</Link>
          </div>
          <div className="muted">
            <Link to={`/objekte/${issue.objectId}`} style={{ color: 'inherit' }}>
              {issue.path.length > 0 ? issue.path.join(' › ') : 'Liegenschaft'}
            </Link>
          </div>
          {issue.occurrenceId && (
            <button type="button" className="linklike small" style={{ marginTop: 4 }} onClick={() => onOpenTask(issue.occurrenceId!)}>
              Zur Aufgabe, bei der das gemeldet wurde
            </button>
          )}
        </div>
        <StatusBadge status={issue.status} />
      </div>

      <table className="data compact" style={{ marginBottom: 12 }}>
        <tbody>
          {isTicket && (
            <tr>
              <td className="muted">Anliegen</td>
              <td>{issue.categoryLabel}</td>
            </tr>
          )}
          <tr>
            <td className="muted">Gemeldet</td>
            <td>
              {issue.reportedByName ?? 'unbekannt'}, {formatDateTime(issue.createdAt)}
            </td>
          </tr>
          {issue.requestedDate && (
            <tr>
              <td className="muted">Wunschtermin</td>
              <td className="strong">{formatDate(issue.requestedDate)}</td>
            </tr>
          )}
          {issue.contact && (
            <tr>
              <td className="muted">Kontakt vor Ort</td>
              <td>{issue.contact}</td>
            </tr>
          )}
          {issue.task && (
            <tr>
              <td className="muted">Aufgabe</td>
              <td>
                <button type="button" className="linklike" onClick={() => onOpenTask(issue.task!.id)}>
                  {issue.task.title}, {formatDate(issue.task.dueDate)}
                </button>{' '}
                <StatusBadge status={issue.task.status} />
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {issue.description && <div className="desc-box">{issue.description}</div>}

      {issue.photos.length > 0 && (
        <div className="photo-grid" style={{ marginBottom: 16 }}>
          {issue.photos.map((photo) => (
            <a key={photo.id} href={`/api/photos/${photo.id}`} target="_blank" rel="noreferrer">
              <img src={`/api/photos/${photo.id}/thumb`} alt="Foto" />
            </a>
          ))}
        </div>
      )}

      {issue.messages.length > 0 && (
        <ul className="thread boxed">
          {issue.messages.map((m) => (
            <li key={m.id} className={`${m.fromCustomer ? 'from-customer' : 'from-office'} ${m.internal ? 'internal' : ''}`}>
              <div className="meta">
                {m.authorName ?? (m.fromCustomer ? 'Kunde' : 'Verwaltung')}, {formatDateTime(m.createdAt)}
                {m.internal ? ', interne Notiz' : ''}
              </div>
              <div className="bubble">{m.body}</div>
            </li>
          ))}
        </ul>
      )}

      {error && <Notice kind="error">{error}</Notice>}

      {closed ? (
        <Notice kind="info">
          Abgeschlossen am {formatDateTime(issue.resolvedAt)}.{issue.resolutionNote ? ` ${issue.resolutionNote}` : ''}
        </Notice>
      ) : planning ? (
        <PlanTask issue={issue} onCancel={() => setPlanning(false)} onDone={async () => { setPlanning(false); await onChanged() }} />
      ) : (
        <>
          <Field label={isTicket ? 'Nachricht an den Kunden oder Notiz' : 'Bearbeitungsnotiz'}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={isTicket ? 'z. B. Passt Ihnen 10 Uhr?' : 'Was wurde veranlasst?'} />
          </Field>
          <div className="actions wrap">
            {isTicket ? (
              <>
                <button type="button" className="btn secondary" disabled={!text.trim() || send.isPending} onClick={() => send.mutate({ body: text })}>
                  <Icon name="send" /> Antworten
                </button>
                <button type="button" className="btn secondary" disabled={!text.trim() || send.isPending} onClick={() => send.mutate({ body: text, status: 'waiting' })}>
                  Rückfrage stellen
                </button>
                <button type="button" className="btn ghost" disabled={!text.trim() || send.isPending} onClick={() => send.mutate({ body: text, internal: true })}>
                  Interne Notiz
                </button>
              </>
            ) : (
              issue.status === 'open' && (
                <button type="button" className="btn secondary" disabled={patch.isPending} onClick={() => patch.mutate({ status: 'in_progress', resolutionNote: text || undefined })}>
                  In Arbeit
                </button>
              )
            )}
            <div className="grow" />
            {!issue.task && (
              <button type="button" className="btn secondary" onClick={() => setPlanning(true)}>
                <Icon name="calendar" /> In Aufgabe überführen
              </button>
            )}
            <button
              type="button"
              className="btn"
              disabled={send.isPending || patch.isPending}
              onClick={() =>
                isTicket ? send.mutate({ body: text.trim() || 'Erledigt.', status: 'done' }) : patch.mutate({ status: 'done', resolutionNote: text || undefined })
              }
            >
              Erledigt
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={send.isPending || patch.isPending || (isTicket && !text.trim())}
              title={isTicket ? 'Bitte eine kurze Begründung für den Kunden schreiben' : undefined}
              onClick={() => (isTicket ? send.mutate({ body: text, status: 'rejected' }) : patch.mutate({ status: 'rejected', resolutionNote: text || undefined }))}
            >
              {isTicket ? 'Ablehnen' : 'Kein Mangel'}
            </button>
          </div>
        </>
      )}
    </>
  )
}

/** Turns the request into a Sonderaufgabe, by default on the wished date. */
function PlanTask({ issue, onCancel, onDone }: { issue: IssueDetail; onCancel: () => void; onDone: () => Promise<void> }) {
  const toast = useToast()
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })
  const [dueDate, setDueDate] = useState(issue.requestedDate && issue.requestedDate >= todayIso() ? issue.requestedDate : todayIso())
  const [templateId, setTemplateId] = useState('')
  const [title, setTitle] = useState(issue.title)
  const [note, setNote] = useState('')
  const [assignment, setAssignment] = useState<Assignment>({ assigneeId: null, assigneeTeamId: null })
  const [error, setError] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () =>
      api(`/api/issues/${issue.id}/task`, {
        method: 'POST',
        body: { dueDate, templateId: templateId || null, title: templateId ? undefined : title, note: note || undefined, ...assignment },
      }),
    onSuccess: async () => {
      toast(issue.kind === 'ticket' ? 'Aufgabe angelegt, Kunde informiert.' : 'Aufgabe angelegt.')
      await onDone()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen.'),
  })
  return (
    <div className="plan-task">
      <div className="strong" style={{ marginBottom: 8 }}>
        In Aufgabe überführen
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      <div className="row">
        <Field label="Datum">
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
        <Field label="Zuständig">
          <AssigneePicker value={assignment} onChange={setAssignment} />
        </Field>
      </div>
      <div className="row">
        <Field label="Leistung">
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Freie Bezeichnung</option>
            {templates.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        {!templateId && (
          <Field label="Bezeichnung">
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        )}
      </div>
      <Field label="Hinweis für den Mitarbeiter" hint="Beschreibung und Kontakt vor Ort werden automatisch mitgegeben.">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
      <div className="actions">
        <button type="button" className="btn" disabled={create.isPending} onClick={() => create.mutate()}>
          Aufgabe anlegen
        </button>
        <button type="button" className="btn secondary" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </div>
  )
}
